import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, RotateCcw, Ban, Loader2, Smartphone, Trash2, Download, Upload, CheckSquare, Square, CalendarPlus } from "lucide-react";
import ThresholdsEditor from "@/components/ThresholdsEditor";
import PushSender from "@/components/PushSender";
import AuditLog from "@/components/AuditLog";

const EXPIRY_OPTIONS = [
  { value: "24h", label: "24 Ore" },
  { value: "7d", label: "7 Giorni" },
  { value: "30d", label: "30 Giorni" },
  { value: "90d", label: "90 Giorni" },
  { value: "365d", label: "365 Giorni" },
  { value: "permanent", label: "Permanente" },
];

const PLAN_BADGE = {
  "ZERO PASS 24H": { short: "24H", cls: "text-sky-300 border-sky-400/50 bg-sky-400/10" },
  "BASIC": { short: "BASIC", cls: "text-[#E3BC55] border-[#E3BC55]/40 bg-[#E3BC55]/10" },
  "PREMIUM": { short: "PREMIUM", cls: "text-fuchsia-300 border-fuchsia-400/50 bg-fuchsia-400/10" },
  "DIAMOND": { short: "DIAMOND", cls: "text-cyan-200 border-cyan-300/50 bg-cyan-300/10" },
  _default: { short: "", cls: "text-slate-300 border-slate-500/40 bg-slate-500/10" },
};

const PLAN_PRICES = { "ZERO PASS 24H": 2.99, "BASIC": 10.99, "PREMIUM": 17.99, "DIAMOND": 59.99 };
const PLAN_ORDER = ["ZERO PASS 24H", "BASIC", "PREMIUM", "DIAMOND"];
const fmtEuro = (n) => n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function AdminDashboard({ onClose }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expiry, setExpiry] = useState("30d");
  const [label, setLabel] = useState("");
  const [count, setCount] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState("");
  const [originFilter, setOriginFilter] = useState("all"); // all | stripe | manual
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/codes").then((r) => setCodes(r.data)).catch(() => toast.error("Errore caricamento codici")).finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const generate = async () => {
    const qty = Math.min(500, Math.max(1, parseInt(count, 10) || 1));
    setGenerating(true);
    try {
      const r = await api.post("/admin/codes", { expiry, label: label.trim() || null, count: qty });
      const created = r.data.codes || [];
      if (created.length === 1) {
        toast.success(`Codice generato: ${created[0].code}`);
      } else {
        toast.success(`${created.length} codici generati con successo`);
      }
      setLabel("");
      load();
    } catch {
      toast.error("Errore generazione codice");
    } finally {
      setGenerating(false);
    }
  };

  const resetDevice = async (c) => {
    await api.post(`/admin/codes/${c.id}/reset-device`);
    toast.success(`Dispositivo resettato per ${c.code}`);
    load();
  };

  const revoke = async (c) => {
    await api.delete(`/admin/codes/${c.id}`);
    toast.success(`Codice ${c.code} revocato`);
    load();
  };

  const hardDelete = async (c) => {
    if (!window.confirm(`Eliminare definitivamente il codice ${c.code}? L'operazione è irreversibile.`)) return;
    await api.delete(`/admin/codes/${c.id}?hard=true`);
    toast.success(`Codice ${c.code} eliminato definitivamente`);
    load();
  };

  const saveLabel = async (c, value) => {
    if ((c.label || "") === value.trim()) return;
    await api.patch(`/admin/codes/${c.id}`, { label: value.trim() });
    toast.success(`Etichetta aggiornata per ${c.code}`);
    load();
  };

  const adjustExpiry = async (c, hours) => {
    try {
      await api.patch(`/admin/codes/${c.id}`, { adjust_hours: hours });
      toast.success(`Durata di ${c.code} ${hours > 0 ? "estesa" : "ridotta"} di ${Math.abs(hours) >= 24 ? Math.abs(hours) / 24 + "g" : Math.abs(hours) + "h"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore modifica durata");
    }
  };

  const changeExpiry = async (c, value) => {
    if (value === "custom") return;
    await api.patch(`/admin/codes/${c.id}`, { expiry: value });
    toast.success(`Scadenza aggiornata per ${c.code}`);
    load();
  };

  const setCustomExpiry = async (c, dateStr) => {
    if (!dateStr) return;
    try {
      await api.patch(`/admin/codes/${c.id}`, { expires_at: `${dateStr}T23:59:59+00:00` });
      toast.success(`Scadenza personalizzata impostata per ${c.code}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Data non valida");
    }
  };

  const filtered = codes.filter((c) => {
    if (originFilter === "stripe" && c.origin !== "STRIPE_AUTO") return false;
    if (originFilter === "manual" && c.origin === "STRIPE_AUTO") return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [c.code, c.label, c.plan, c.customer_email, c.origin]
      .some((v) => (v || "").toString().toLowerCase().includes(q));
  });

  const stripeCount = codes.filter((c) => c.origin === "STRIPE_AUTO").length;

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));
  const toggleAll = () => {
    setSelected((prev) => {
      if (filtered.every((c) => prev.has(c.id))) {
        const next = new Set(prev);
        filtered.forEach((c) => next.delete(c.id));
        return next;
      }
      const next = new Set(prev);
      filtered.forEach((c) => next.add(c.id));
      return next;
    });
  };

  const selectedCodes = () => codes.filter((c) => selected.has(c.id));

  const runBulk = async (fn, successMsg) => {
    const targets = selectedCodes();
    if (!targets.length) return;
    setBulkBusy(true);
    let ok = 0;
    for (const c of targets) {
      try { await fn(c); ok++; } catch { /* skip */ }
    }
    setBulkBusy(false);
    setSelected(new Set());
    toast.success(successMsg(ok));
    load();
  };

  const bulkExtend = (hours) => runBulk(
    (c) => api.patch(`/admin/codes/${c.id}`, { adjust_hours: hours }),
    (n) => `Durata estesa per ${n} codici`,
  );
  const bulkRevoke = () => runBulk(
    (c) => api.delete(`/admin/codes/${c.id}`),
    (n) => `${n} codici revocati`,
  );
  const bulkDelete = () => {
    if (!window.confirm(`Eliminare definitivamente ${selected.size} codici? Operazione irreversibile.`)) return;
    return runBulk(
      (c) => api.delete(`/admin/codes/${c.id}?hard=true`),
      (n) => `${n} codici eliminati`,
    );
  };

  const exportCsv = () => {
    const rows = [["codice", "etichetta", "stato", "origine", "piano", "email_cliente", "stato_email", "dispositivo_associato", "scadenza", "ultimo_accesso"]];
    const nowT = Date.now();
    filtered.forEach((c) => {
      const stato = c.revoked ? "revocato" : (c.expires_at && new Date(c.expires_at).getTime() <= nowT ? "scaduto" : "attivo");
      rows.push([
        c.code,
        (c.label || "").replace(/"/g, "'"),
        stato,
        c.origin || "MANUAL",
        c.plan || "",
        c.customer_email || "",
        c.email_status || "",
        c.device_hash ? "si" : "no",
        c.expires_at || "permanente",
        c.last_login_at || "",
      ]);
    });
    const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `zero-signals-codici-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Esportati ${filtered.length} codici`);
  };

  const exportStripeCsv = () => {
    const stripeCodes = codes.filter((c) => c.origin === "STRIPE_AUTO");
    if (stripeCodes.length === 0) {
      toast.error("Nessun cliente Stripe da esportare");
      return;
    }
    const rows = [["email_cliente", "piano", "codice", "stato_email", "scadenza", "creato_il", "ultimo_accesso"]];
    stripeCodes.forEach((c) => {
      rows.push([
        c.customer_email || "",
        c.plan || "",
        c.code,
        c.email_status || "",
        c.expires_at || "permanente",
        c.created_at || "",
        c.last_login_at || "",
      ]);
    });
    const csv = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `zero-signals-clienti-stripe-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Esportati ${stripeCodes.length} clienti Stripe`);
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const validExpiry = ["24h", "7d", "30d", "permanent"];
      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      let created = 0;
      for (let i = 0; i < lines.length; i++) {
        const cols = lines[i].split(/[,;]/).map((c) => c.trim().replace(/^"|"$/g, ""));
        const first = (cols[0] || "").toLowerCase();
        if (i === 0 && (first === "etichetta" || first === "label" || first === "codice")) continue;
        const lbl = cols[0] || null;
        const exp = validExpiry.includes((cols[1] || "").toLowerCase()) ? cols[1].toLowerCase() : "30d";
        try {
          await api.post("/admin/codes", { expiry: exp, label: lbl, count: 1 });
          created++;
        } catch { /* skip row */ }
      }
      toast.success(`Import completato: ${created} codici creati`);
      load();
    } catch {
      toast.error("Errore lettura file CSV");
    } finally {
      setImporting(false);
    }
  };

  const now_ts = Date.now();
  const stats = {
    total: codes.length,
    active: codes.filter((c) => !c.revoked && (!c.expires_at || new Date(c.expires_at).getTime() > now_ts)).length,
    associated: codes.filter((c) => !!c.device_hash).length,
  };

  const revenue = PLAN_ORDER.map((p) => {
    const count = codes.filter((c) => c.origin === "STRIPE_AUTO" && c.plan === p).length;
    return { plan: p, count, total: count * (PLAN_PRICES[p] || 0) };
  });
  const revenueTotal = revenue.reduce((s, r) => s + r.total, 0);
  const salesTotal = revenue.reduce((s, r) => s + r.count, 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4">
      <div data-testid="admin-modal" className="w-full max-w-6xl bg-black border border-amber-500/25 rounded-md gold-glow fade-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-amber-500/15">
          <h2 className="font-heading font-bold text-lg text-white uppercase tracking-tight">Dashboard Amministrativa · Codici ZS</h2>
          <button data-testid="admin-close-button" onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div data-testid="admin-stats-bar" className="grid grid-cols-3 gap-3">
            {[
              { key: "total", label: "Codici Totali", value: stats.total, testid: "admin-stat-total" },
              { key: "active", label: "Attivi", value: stats.active, testid: "admin-stat-active" },
              { key: "associated", label: "Associati", value: stats.associated, testid: "admin-stat-associated" },
            ].map((s) => (
              <div key={s.key} data-testid={s.testid} className="bg-black border border-amber-500/15 rounded-md px-4 py-3 text-center">
                <div className="font-mono-data text-2xl sm:text-3xl font-bold text-amber-300">{s.value}</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-1">{s.label}</div>
              </div>
            ))}
          </div>

          <div data-testid="admin-revenue-panel" className="bg-black border border-amber-500/15 rounded-md p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[11px] text-slate-400 uppercase tracking-widest">Riepilogo Incassi · Stripe</h3>
              <div className="text-right">
                <div data-testid="admin-revenue-total" className="font-mono-data text-xl font-bold text-[#E3BC55]">{fmtEuro(revenueTotal)}</div>
                <div className="text-[9px] text-slate-500 uppercase tracking-widest">{salesTotal} vendite · stima</div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {revenue.map((r) => (
                <div key={r.plan} data-testid={`admin-revenue-${r.plan.replace(/\s+/g, "-")}`} className={`rounded-md border px-3 py-2.5 ${(PLAN_BADGE[r.plan] || PLAN_BADGE._default).cls}`}>
                  <div className="text-[9px] uppercase tracking-widest opacity-80 truncate">{(PLAN_BADGE[r.plan] || { short: r.plan }).short || r.plan}</div>
                  <div className="font-mono-data text-lg font-bold mt-0.5">{r.count}</div>
                  <div className="text-[10px] opacity-90">{fmtEuro(r.total)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3 bg-black border border-amber-500/15 rounded-md p-4">
            <div>
              <label className="text-[11px] text-slate-400 uppercase tracking-wider block mb-1.5">Scadenza nuovo codice</label>
              <select
                data-testid="admin-code-expiry-select"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                className="bg-black border border-amber-500/25 rounded-sm px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-amber-400"
              >
                {EXPIRY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-400 uppercase tracking-wider block mb-1.5">Etichetta (opzionale)</label>
              <input
                data-testid="admin-code-label-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Es. Cliente Marco"
                maxLength={40}
                className="bg-black border border-amber-500/25 rounded-sm px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-amber-400 w-44"
              />
            </div>
            <div>
              <label className="text-[11px] text-slate-400 uppercase tracking-wider block mb-1.5">Quantità (1-500)</label>
              <input
                data-testid="admin-code-count-input"
                type="number"
                min={1}
                max={500}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                onBlur={(e) => setCount(Math.min(500, Math.max(1, parseInt(e.target.value, 10) || 1)))}
                className="bg-black border border-amber-500/25 rounded-sm px-3 py-2 font-mono-data text-sm text-amber-200 focus:outline-none focus:border-amber-400 w-24 text-center"
              />
            </div>
            <button
              data-testid="admin-generate-code-button"
              onClick={generate}
              disabled={generating}
              className="px-4 py-2 rounded-sm bg-gradient-to-r from-[#D4AF37] to-[#B8952E] text-black font-heading font-bold uppercase tracking-widest text-xs hover:from-[#E5C158] hover:to-[#D4AF37] transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} {parseInt(count, 10) > 1 ? `Genera ${count} Codici` : "Genera Codice"}
            </button>
            <div data-testid="admin-origin-filter" className="ml-auto flex items-center gap-1 bg-black border border-amber-500/20 rounded-sm p-0.5">
              {[
                { key: "all", label: "Tutti" },
                { key: "stripe", label: `Stripe${stripeCount ? ` (${stripeCount})` : ""}` },
                { key: "manual", label: "Manuali" },
              ].map((f) => (
                <button
                  key={f.key}
                  data-testid={`admin-origin-filter-${f.key}`}
                  onClick={() => setOriginFilter(f.key)}
                  className={`px-2.5 py-1.5 rounded-sm text-[10px] font-heading uppercase tracking-wider transition-colors ${
                    originFilter === f.key
                      ? "bg-gradient-to-r from-[#D4AF37] to-[#B8952E] text-black font-bold"
                      : "text-slate-400 hover:text-amber-300"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <input
              data-testid="admin-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca codice, email, piano…"
              className="bg-black border border-amber-500/25 rounded-sm px-3 py-2 font-mono-data text-sm text-amber-200 focus:outline-none focus:border-amber-400 w-40"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="admin-export-csv-button"
              onClick={exportCsv}
              className="flex items-center gap-1.5 text-[11px] font-heading uppercase tracking-wider text-slate-300 border border-slate-600/50 rounded-sm px-3 py-1.5 hover:text-amber-300 hover:border-amber-500/40 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Esporta CSV
            </button>
            <button
              data-testid="admin-export-stripe-button"
              onClick={exportStripeCsv}
              className="flex items-center gap-1.5 text-[11px] font-heading uppercase tracking-wider text-[#E3BC55] border border-[#E3BC55]/40 rounded-sm px-3 py-1.5 hover:bg-[#E3BC55]/10 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Esporta Clienti Stripe
            </button>
            <label
              data-testid="admin-import-csv-label"
              className="flex items-center gap-1.5 text-[11px] font-heading uppercase tracking-wider text-slate-300 border border-slate-600/50 rounded-sm px-3 py-1.5 hover:text-amber-300 hover:border-amber-500/40 transition-colors cursor-pointer"
            >
              {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Importa CSV
              <input data-testid="admin-import-csv-input" type="file" accept=".csv,text/csv" onChange={importCsv} className="hidden" />
            </label>
            <span className="text-[10px] text-slate-600">CSV: colonna 1 = etichetta, colonna 2 = scadenza (24h/7d/30d/permanent)</span>

            {selected.size > 0 && (
              <div data-testid="admin-bulk-toolbar" className="flex flex-wrap items-center gap-2 ml-auto bg-amber-500/[0.06] border border-amber-500/25 rounded-md px-3 py-1.5">
                <span className="text-[11px] font-mono-data text-amber-300">{selected.size} selezionati</span>
                <button data-testid="admin-bulk-extend-7d" disabled={bulkBusy} onClick={() => bulkExtend(168)} className="flex items-center gap-1 text-[10px] uppercase text-emerald-300 border border-emerald-500/30 rounded-sm px-2 py-1 hover:border-emerald-400 transition-colors disabled:opacity-50"><CalendarPlus className="w-3 h-3" /> +7g</button>
                <button data-testid="admin-bulk-extend-30d" disabled={bulkBusy} onClick={() => bulkExtend(720)} className="flex items-center gap-1 text-[10px] uppercase text-emerald-300 border border-emerald-500/30 rounded-sm px-2 py-1 hover:border-emerald-400 transition-colors disabled:opacity-50"><CalendarPlus className="w-3 h-3" /> +30g</button>
                <button data-testid="admin-bulk-revoke" disabled={bulkBusy} onClick={bulkRevoke} className="flex items-center gap-1 text-[10px] uppercase text-amber-300 border border-amber-500/30 rounded-sm px-2 py-1 hover:border-amber-400 transition-colors disabled:opacity-50"><Ban className="w-3 h-3" /> Revoca</button>
                <button data-testid="admin-bulk-delete" disabled={bulkBusy} onClick={bulkDelete} className="flex items-center gap-1 text-[10px] uppercase text-red-400 border border-red-500/30 rounded-sm px-2 py-1 hover:border-red-400 transition-colors disabled:opacity-50"><Trash2 className="w-3 h-3" /> Elimina</button>
                {bulkBusy && <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-400" />}
              </div>
            )}
          </div>

          <div data-testid="admin-codes-table" className="overflow-x-auto border border-amber-500/10 rounded-md">
            {loading ? (
              <div className="p-8 text-center font-mono-data text-sm text-amber-500/70">CARICAMENTO…</div>
            ) : (
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="border-b border-amber-500/15 bg-black">
                    <th className="px-3 py-2.5 w-10">
                      <button data-testid="admin-select-all" onClick={toggleAll} className="text-amber-400/80 hover:text-amber-300 transition-colors">
                        {allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                    {["Codice", "Etichetta", "Stato", "Dispositivo", "Scadenza", "Ultimo accesso", "Azioni"].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 font-mono-data text-[10px] text-amber-400/80 uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-600">Nessun codice presente. Genera il primo codice.</td></tr>
                  )}
                  {filtered.map((c) => {
                    const expired = c.expires_at && new Date(c.expires_at) <= new Date();
                    return (
                      <tr key={c.id} className={`border-b border-amber-500/[0.06] ${selected.has(c.id) ? "bg-amber-500/[0.04]" : ""}`}>
                        <td className="px-3 py-3">
                          <button data-testid={`admin-select-${c.code}`} onClick={() => toggleSelect(c.id)} className="text-amber-400/70 hover:text-amber-300 transition-colors">
                            {selected.has(c.id) ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-3 font-mono-data text-sm text-amber-300">
                          <div>{c.code}</div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {c.origin === "STRIPE_AUTO" ? (
                              <>
                                <span data-testid={`admin-origin-${c.code}`} className="text-[9px] font-heading uppercase tracking-wider text-[#E3BC55] border border-[#E3BC55]/40 rounded px-1.5 py-0.5" title={c.customer_email || ""}>Stripe</span>
                                {c.plan && (
                                  <span data-testid={`admin-plan-${c.code}`} className={`text-[9px] font-heading uppercase tracking-wider rounded px-1.5 py-0.5 border ${(PLAN_BADGE[c.plan] || PLAN_BADGE._default).cls}`} title={`Piano ${c.plan}`}>
                                    {(PLAN_BADGE[c.plan] || { short: c.plan }).short}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span data-testid={`admin-origin-${c.code}`} className="text-[9px] font-heading uppercase tracking-wider text-slate-500 border border-slate-600/40 rounded px-1.5 py-0.5">Manuale</span>
                            )}
                            {c.email_status === "EMAIL_FAILED" && (
                              <span data-testid={`admin-emailko-${c.code}`} className="text-[9px] font-heading uppercase tracking-wider text-red-400 border border-red-500/40 rounded px-1.5 py-0.5" title={c.customer_email || ""}>Email KO</span>
                            )}
                            {c.email_status === "SENT" && (
                              <span className="text-[9px] font-heading uppercase tracking-wider text-emerald-400/80 border border-emerald-500/30 rounded px-1.5 py-0.5">Email OK</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            data-testid={`admin-label-input-${c.code}`}
                            defaultValue={c.label || ""}
                            placeholder="—"
                            maxLength={40}
                            onBlur={(e) => saveLabel(c, e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
                            className="w-28 bg-black border border-amber-500/20 rounded-sm px-2 py-1 text-xs text-slate-300 focus:outline-none focus:border-amber-400"
                          />
                        </td>
                        <td className="px-4 py-3">
                          {c.revoked ? (
                            <span className="text-[10px] font-mono-data uppercase text-red-400 border border-red-500/30 rounded-full px-2 py-0.5">Revocato</span>
                          ) : expired ? (
                            <span className="text-[10px] font-mono-data uppercase text-orange-400 border border-orange-500/30 rounded-full px-2 py-0.5">Scaduto</span>
                          ) : (
                            <span className="text-[10px] font-mono-data uppercase text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5">Attivo</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {c.device_hash ? (
                            <span className="flex items-center gap-1.5 text-xs text-slate-300">
                              <Smartphone className="w-3.5 h-3.5 text-emerald-400" />
                              <span className="font-mono-data">{c.device_hash.slice(0, 10)}…</span>
                            </span>
                          ) : (
                            <span className="text-xs text-slate-600">Non associato</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={c.expiry_preset || "30d"}
                            onChange={(e) => changeExpiry(c, e.target.value)}
                            disabled={c.revoked}
                            className="bg-black border border-amber-500/20 rounded-sm px-2 py-1 text-xs text-slate-300 focus:outline-none focus:border-amber-400 disabled:opacity-40"
                          >
                            {EXPIRY_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                            <option value="custom">Personalizzata</option>
                          </select>
                          <input
                            type="date"
                            data-testid={`admin-custom-expiry-${c.code}`}
                            disabled={c.revoked}
                            defaultValue={c.expires_at ? c.expires_at.slice(0, 10) : ""}
                            onChange={(e) => setCustomExpiry(c, e.target.value)}
                            className="block mt-1 bg-black border border-amber-500/20 rounded-sm px-2 py-0.5 text-[11px] text-slate-400 focus:outline-none focus:border-amber-400 disabled:opacity-40 [color-scheme:dark]"
                          />
                          <div className="flex gap-1 mt-1">
                            {[{ h: -24, l: "-1g" }, { h: 24, l: "+1g" }, { h: -1, l: "-1h" }, { h: 1, l: "+1h" }].map((b) => (
                              <button
                                key={b.l}
                                data-testid={`admin-adjust-${b.l}-${c.code}`}
                                onClick={() => adjustExpiry(c, b.h)}
                                disabled={c.revoked}
                                className={`text-[10px] font-mono-data border rounded-sm px-1.5 py-0.5 transition-colors disabled:opacity-30 ${
                                  b.h > 0
                                    ? "text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"
                                    : "text-red-400 border-red-500/30 hover:bg-red-500/10"
                                }`}
                              >
                                {b.l}
                              </button>
                            ))}
                          </div>
                          <span className="block font-mono-data text-[10px] text-slate-600 mt-1">{c.expires_at ? fmtDate(c.expires_at) : "Permanente"}</span>
                        </td>
                        <td className="px-4 py-3 font-mono-data text-xs text-slate-500">{fmtDate(c.last_login_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              data-testid={`admin-device-reset-button-${c.code}`}
                              onClick={() => resetDevice(c)}
                              disabled={!c.device_hash || c.revoked}
                              className="flex items-center gap-1 text-[10px] font-mono-data uppercase text-amber-400 border border-amber-500/30 rounded-sm px-2 py-1 hover:bg-amber-500/10 transition-colors disabled:opacity-30"
                            >
                              <RotateCcw className="w-3 h-3" /> Reset
                            </button>
                            <button
                              data-testid={`admin-revoke-button-${c.code}`}
                              onClick={() => revoke(c)}
                              disabled={c.revoked}
                              className="flex items-center gap-1 text-[10px] font-mono-data uppercase text-red-400 border border-red-500/30 rounded-sm px-2 py-1 hover:bg-red-500/10 transition-colors disabled:opacity-30"
                            >
                              <Ban className="w-3 h-3" /> Revoca
                            </button>
                            <button
                              data-testid={`admin-delete-button-${c.code}`}
                              onClick={() => hardDelete(c)}
                              className="flex items-center gap-1 text-[10px] font-mono-data uppercase text-red-300 border border-red-500/50 bg-red-500/10 rounded-sm px-2 py-1 hover:bg-red-500/25 transition-colors"
                            >
                              <Trash2 className="w-3 h-3" /> Elimina
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <PushSender />

          <ThresholdsEditor />

          <AuditLog />
        </div>
      </div>
    </div>
  );
}
